import { Link } from 'react-router-dom';
import { Phone, ChevronRight } from 'lucide-react';
import NavShell from '../components/NavShell';

export default function SmsTermsPage() {
  return (
    <NavShell>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <h1 className="text-3xl font-bold text-[#111827]">SMS Communications &amp; Opt-In</h1>
        <p className="text-sm text-gray-500">Last updated: April 20, 2026</p>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">1. Overview</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Swipe Opinion, a DBA of One Nice Photo, LLC, offers SMS notifications to
            members who explicitly opt in during account registration. This page documents our
            SMS program, opt-in process, and the disclosures provided to members at the point
            of consent. Two distinct SMS use cases are supported: (1) one-time verification
            codes for account login and phone verification, and (2) opt-in notifications about
            surveys, streaks, expiring points, and redemptions.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">2. How Members Opt In</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            After account creation, members proceed through an onboarding flow. One step is
            titled &ldquo;Add your phone number.&rdquo; Members must:
          </p>
          <ol className="list-decimal list-inside text-sm text-[#111827]/80 space-y-1 ml-2">
            <li>Enter a valid US phone number</li>
            <li>Check a consent checkbox containing the full TCPA disclosure (reproduced below in Section 3)</li>
            <li>Verify the number by entering a 6-digit code sent via SMS</li>
          </ol>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Opting in is optional. Members can skip this step and continue using the platform
            without SMS. Consent is recorded in our database with timestamp and IP address at
            the moment the checkbox is confirmed.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">3. Consent Language (verbatim)</h2>
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            <p className="text-sm text-[#111827] italic leading-relaxed">
              &ldquo;By checking this box, I agree to receive recurring automated marketing
              text messages from Swipe Opinion at the number provided. Consent is not a
              condition of purchase. Msg frequency varies. Msg &amp; data rates may apply.
              Reply STOP to cancel, HELP for help.&rdquo;
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">
            4. Opt-In Proof &mdash; Login Page (Use Case: Auth OTP)
          </h2>
          <h3 className="text-base font-semibold text-[#111827]">
            Login Page &mdash; Phone-Based Account Access
          </h3>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Members can sign in to their account using a phone number. Upon entering their
            number and clicking &ldquo;Send verification code,&rdquo; they receive a one-time
            passcode via SMS. This is a transactional authentication message, not a marketing
            message.
          </p>
          <figure className="my-6 border border-gray-300 rounded-lg overflow-hidden">
            <img
              src="/sms-terms/login-page-screenshot.jpg"
              alt="Swipe Opinion login page showing phone-based sign-in option with Terms of Service and Privacy Policy clickwrap"
              className="w-full h-auto"
              loading="lazy"
            />
            <figcaption className="text-sm text-gray-600 p-3 bg-gray-50 border-t border-gray-300">
              Screenshot: Public login page at swipeopinion.com/login showing phone number
              input for account authentication and Terms of Service / Privacy Policy clickwrap.
            </figcaption>
          </figure>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">
            5. Opt-In Proof &mdash; Onboarding (Use Case: Marketing Opt-In)
          </h2>
          <h3 className="text-base font-semibold text-[#111827]">
            Onboarding &mdash; SMS Notifications Opt-In
          </h3>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            During account onboarding, members are presented with a dedicated step where they
            may opt in to receive SMS notifications about surveys and account activity. The
            step is explicitly optional &mdash; members can skip it and continue using the
            platform without providing a phone number or opting in.
          </p>
          <figure className="my-6 border border-gray-300 rounded-lg overflow-hidden">
            <img
              src="/sms-terms/opt-in-screenshot.jpg"
              alt="Swipe Opinion onboarding phone number step with TCPA-compliant SMS opt-in checkbox"
              className="w-full h-auto"
              loading="lazy"
            />
            <figcaption className="text-sm text-gray-600 p-3 bg-gray-50 border-t border-gray-300">
              Screenshot: Onboarding &ldquo;Add your phone number&rdquo; step showing the
              TCPA-compliant opt-in checkbox, disclosure language, and &ldquo;Skip for
              now&rdquo; option.
            </figcaption>
          </figure>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">
            6. Opt-In Proof &mdash; Visual Replica (Fallback)
          </h2>
          <h3 className="text-base font-semibold text-[#111827]">
            Visual Replica of Onboarding Opt-In Form
          </h3>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            The following is a non-functional, read-only visual replica of the opt-in form
            shown in the screenshot above, provided as a fallback in case the image does not
            load.
          </p>

          <div
            className="border border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 pointer-events-none select-none"
            aria-hidden="true"
          >
            <div className="max-w-sm mx-auto bg-white rounded-2xl shadow-sm p-6 space-y-6">
              <div className="text-center">
                <Phone className="w-10 h-10 text-[#5B4FE8] mx-auto mb-2" />
                <h2 className="text-xl font-bold text-[#111827]">Add your phone number</h2>
                <p className="text-sm text-[#6B7280] mt-1">
                  Get instant alerts when high-value surveys are available. Optional &mdash;
                  you can skip this.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">
                  Phone number
                </label>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 px-3 py-2.5 bg-gray-50 border border-[#E5E7EB] rounded-xl text-sm text-[#6B7280]">
                    <span className="text-base">&#x1F1FA;&#x1F1F8;</span> +1
                  </span>
                  <input
                    type="tel"
                    disabled
                    readOnly
                    value=""
                    placeholder="(555) 555-1234"
                    className="flex-1 border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-sm bg-white"
                  />
                </div>
              </div>

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  disabled
                  readOnly
                  checked={false}
                  className="mt-0.5 w-4 h-4 rounded border-[#D1D5DB] text-[#5B4FE8]"
                />
                <span className="text-xs text-[#6B7280] leading-relaxed">
                  By checking this box, I agree to receive recurring automated marketing text
                  messages from Swipe Opinion at the number provided. Consent is not a condition
                  of purchase. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP
                  to cancel, HELP for help.
                </span>
              </label>

              <button
                disabled
                className="w-full py-3 bg-[#5B4FE8] text-white font-medium rounded-full opacity-60 flex items-center justify-center gap-2"
              >
                <ChevronRight className="w-4 h-4" />
                Send verification code
              </button>

              <div className="w-full text-center text-sm text-[#6B7280] py-2 underline">
                Skip for now
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">7. Sample Messages</h2>

          <p className="text-sm font-medium text-[#111827]">Example OTP message:</p>
          <div className="max-w-md bg-gray-100 rounded-2xl rounded-bl-sm p-4 my-3 font-mono text-sm text-[#111827]">
            Your Swipe Opinion verification code is 123456. Expires in 5 minutes.
          </div>

          <p className="text-sm font-medium text-[#111827] mt-4">Example survey alert:</p>
          <div className="max-w-md bg-gray-100 rounded-2xl rounded-bl-sm p-4 my-3 font-mono text-sm text-[#111827]">
            [Swipe Opinion] You have surveys waiting &mdash; earn up to $3.02 today. Visit
            swipeopinion.com/surveys to start. Reply STOP to unsubscribe.
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">
            8. Message Frequency &amp; Rates
          </h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Members who opt in receive up to 5 messages per week depending on survey
            availability and account activity. Message and data rates may apply. Swipe Opinion
            does not charge members for SMS; standard carrier rates may apply depending on the
            member&rsquo;s mobile plan.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">9. Opting Out</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Members can unsubscribe at any time by:
          </p>
          <ul className="list-disc list-inside text-sm text-[#111827]/80 space-y-1 ml-2">
            <li>Replying STOP to any SMS message</li>
            <li>
              Turning off SMS notifications in their profile settings at
              swipeopinion.com/profile
            </li>
            <li>
              Contacting support at{' '}
              <a
                href="mailto:support@swipeopinion.com"
                className="text-[#5B4FE8] hover:underline"
              >
                support@swipeopinion.com
              </a>
            </li>
          </ul>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Opting out of SMS does not affect account status or access to the platform.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">10. Help</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            For help, reply HELP to any SMS or email{' '}
            <a
              href="mailto:support@swipeopinion.com"
              className="text-[#5B4FE8] hover:underline"
            >
              support@swipeopinion.com
            </a>
            .
          </p>
        </section>

        <section className="space-y-3 pb-8">
          <h2 className="text-xl font-semibold text-[#111827]">11. Related Policies</h2>
          <ul className="list-disc list-inside text-sm text-[#111827]/80 space-y-1 ml-2">
            <li>
              Privacy Policy:{' '}
              <Link to="/privacy" className="text-[#5B4FE8] hover:underline">
                /privacy
              </Link>
            </li>
            <li>
              Terms &amp; Conditions:{' '}
              <Link to="/terms" className="text-[#5B4FE8] hover:underline">
                /terms
              </Link>
            </li>
          </ul>
        </section>
      </main>
    </NavShell>
  );
}
